const libFableService = require('fable-serviceproviderbase');

class HeadlightModalFableService extends libFableService
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/**
		 * @type {import('fable') & { ContentAssignment: import('pict/types/source/Pict-Content-Assignment') } & { EntityProvider: any, HeadlightPopover: any, addTemplate: any, TemplateProvider: any, HeadlightRestClient: any, MetaTemplate: any, manifest: any, resolveStateFromAddress: any, ManifestPackagingService: any, parseTemplate: any }}}
		 */
		this.fable;

		this.serviceType = 'QuickSummaries';

        this.typeConfig = {};
        this.dateFormat = 'YYYY-MM-DD';
        this.decimalPrecision = 2;
        this.typeIconMap =
        {
            'LineItem': 'list-tree',
            'Project': 'folder-open',
            'Sample': 'qr-code',
            'User': 'user',
            'Document': 'file-lines',
            'TestInstance': 'microscope',
            'Test': 'clipboard-list-check',
            'Location': 'location-dot',
            'ProductionPlant': 'industry',
            'MixDesign': 'vial',
            'Material': 'boxes-packing',
            'Supplier': 'truck-fast',
            'Producer': 'tractor',
            'Bridge': 'bridge',
        };
        this.fable.MetaTemplate.addPattern('{~QuickSummary:', '~}', (pHash, pData) =>
		{
            const tokens = pHash?.split('^');
            if (tokens.length !== 2)
            {
                console.log('[QuickSummariesService] Cannot render quick summary, missing either an entity or an ID.');
                return '';
            }
			const value = !isNaN(tokens[1]) ? tokens[1] : this.fable.resolveStateFromAddress(tokens[1], pData);
            const type = tokens[0];
            const uuid = this.fable.getUUID();
            return /*html*/`
                <button
                    type="button"
                    class="button is-ghost has-text-info p-0 m-0 is-inline-block"
                    style="font-size: inherit; line-height: inherit;"
                    id="${ type }-${ uuid }"
                    aria-haspopup="true"
                    onmouseover="_Pict.fable.services.HeadlightQuickSummaries.spawnQuickSummary({ ID: ${ value }, parentID: '${ type }-${ uuid }', type: '${ type }'})"
                    onclick="_Pict.fable.services.HeadlightQuickSummaries.spawnQuickSummary({ ID: ${ value }, parentID: '${ type }-${ uuid }', type: '${ type }'})">
                    <i class="fas fa-${ this.typeIconMap[type] || 'circle-info' }" aria-hidden="true"></i>
                </button>
            `;
		});
	}

    _linkBuilder(type, field, record, template)
    {
        const baseURL = window.location.origin.includes('localhost') ? window.location.origin.replaceAll(window.location.origin.split('.')[1], 'qa.headlight.com') : window.location.origin;
        if (type == 'Document')
        {
            template = template || `https://links${ baseURL.replaceAll(baseURL.split('.')[0], '') }/document/{~D:Record.${ field }~}`;
        }
        else
        {
            template = template || `https://home${ baseURL.replaceAll(baseURL.split('.')[0], '') }/customer/{~D:Record.IDCustomer~}/master-list/${ type.toLowerCase() }/{~D:Record.${ field }~}`;
        }
        if (template)
        {
            return this.fable.parseTemplate(template, record);
        }
        return '';
    }

    applyTypeConfig(tc)
    {
        this.typeConfig = Object.assign(this.typeConfig, tc);
    }

    addManifestForType(type, manifest)
    {
        this.typeConfig[type] = manifest;
    }

    _getTypeSchema(type)
    {
		return new Promise((resolve, reject) =>
		{
			this.fable.HeadlightRestClient.getJSON(`${ type }/Schema`, (pError, pBody) =>
			{
				if (pError)
				{
					this.fable.log.error(`Error getting schema: `, pError);
					return reject(pError);
				}
                if (!pBody?.MeadowSchema?.Schema)
                {
                    pBody.MeadowSchema = 
                    {
                        Schema: []
                    };
                    // If no meadow schema, jimmy the properties into the same format to make consumption easier.
                    for (const p of Object.keys(pBody.properties))
                    {
                        pBody.MeadowSchema.Schema.push(
                        {
                            Column: p,
                            Type: pBody.properties[p].type
                        });
                    }
                }
				resolve(pBody?.MeadowSchema?.Schema);
			});
		});
    }

    async _generateDefaultManifest(type)
    {
        const schema = await this._getTypeSchema(type);
        const tempManifest = 
        {
            Scope: `${ type }-Summary`,
	        TitleTemplate: this._getHumanReadableFieldName(type),
            Descriptors: {}
        };
        for (const field of schema)
        {
            const f = field.Column;
            // Ignore IDs and delete auditing fields plus any fields configured to exclude.
            if (f == `ID${ type }` || f == `GUID${ type }` || f.startsWith('Delet'))
            {
                continue;
            }

            // Resolve IDs
            else if (f.includes('ID') && f.split('ID')?.['1'] && !f.includes('GUID'))
            {
                const cleanedField = '_' + f.replaceAll('ID', '');
                tempManifest.Descriptors[cleanedField] =
                {
                    Hash: cleanedField,
                    Name: this._getHumanReadableFieldName(f)
                };
            }
            else if (f.includes('Date') || f.includes('Time') || field.Type.includes('Date') || field.Type.includes('Time'))
            {
                tempManifest.Descriptors[`${ f }_Formatted`] =
                {
                    Hash: `${ f }_Formatted`,
                    Name: this._getHumanReadableFieldName(f),
                    DataType: 'DateTime',
                    HeadlightSummary:
                    {
                        DateFormat: this.dateFormat
                    }
                };
            }
            else if (['number', 'Decimal'].includes(field.Type))
            {
                tempManifest.Descriptors[`${ f }_Formatted`] =
                {
                    Hash: `${ f }_Formatted`,
                    Name: this._getHumanReadableFieldName(f),
                    DataType: 'PreciseNumber',
                    HeadlightSummary:
                    {
                        DecimalPrecision: 2
                    }
                };
            }
            else
            {
                tempManifest.Descriptors[f] =
                {
                    Hash: f,
                    Name: this._getHumanReadableFieldName(f),
                };
            }
        }
        return tempManifest;
    }

    async _getEntity(type, ID, full, displayFields)
    {
        return await new Promise((resolve, reject) => 
        {
            this.fable.EntityProvider.getEntity(type, ID, (err, res) =>
            {
                if (err || res.Error)
                {
                    return resolve('');
                }
                if (full)
                {
                    return resolve(res);
                }
                if (displayFields)
                {
                    const displayStrings = [];
                    displayFields.forEach((f) =>
                    {
                        displayStrings.push(res[f] || '');   
                    });
                    return resolve(displayStrings.join(' '));
                }
                if (type == 'User')
                {
                    return resolve(`${ res.NameFirst || '' } ${ res.NameLast || '' }`);
                }
                resolve(res.Name || res.QRcode || res[`GUID${ type }`] || '');
            })
        });
    }

	async processRecord(type, record, action = 'Summary', typeConfig = this.typeConfig)
    {
        const decoratedRecord = {};
        if (typeConfig[type]?.Descriptors)
        {
            for (let k of Object.keys(typeConfig[type].Descriptors))
            {
                if (k.includes('JSON'))
                {
                    record[k] = 'JSON Object';
                }
            }
        }
        for (const f of Object.keys(record))
        {
            if (f == `ID${ type }`)
            {
                continue;
            }
            else if (f.includes('ID') && f.split('ID')?.['1'] && !f.includes('GUID'))
            {
                // Resolve IDs
                const cleanedField = '_' + f.replaceAll('ID', '');
                if (record[f])
                {
                    try
                    {
                        decoratedRecord[cleanedField] = await this._getEntity(f.split('ID')[1], record[f], false, typeConfig[type]?.Descriptors?.[cleanedField]?.[`Headlight${ action }`]?.DisplayFields);
                        decoratedRecord[`${ cleanedField }_Link`] = this._linkBuilder(f.split('ID')[1], f, record, typeConfig[type]?.Descriptors?.[cleanedField]?.[`Headlight${ action }`]?.LinkTemplate);
                        continue;
                    }
                    catch (e)
                    {
                        console.log('Error fetching record: ', e);
                    }
                }
                decoratedRecord[cleanedField] = 'N/A';
            }
            else if (typeConfig[type]?.Descriptors?.[`${ f }_Formatted`]?.DataType == 'DateTime')
            {
                decoratedRecord[`${ f }_Formatted`] = record[f] ? this.fable.Dates.dayJS(record[f]).format(typeConfig[type]?.Descriptors?.[f]?.[`Headlight${ action }`]?.DateFormat || this.dateFormat) : 'N/A';
            }
            else if (typeConfig[type]?.Descriptors?.[`${ f }_Formatted`]?.DataType == 'PreciseNumber')
            {

                let tmpRoundingMethod = this.fable.Math.roundHalfUp;
                if (typeConfig[type]?.Descriptors?.[f]?.[`Headlight${ action }`]?.RoundingMethod)
                {
                    try
                    {
                        tmpRoundingMethod = parseInt(typeConfig[type].Descriptors[f][`Headlight${ action }`].RoundingMethod);
                    }
                    catch(pError)
                    {
                        this.log.error(`Error parsing rounding method: `, pError);
                    }
                }
                decoratedRecord[`${ f }_Formatted`] = this.fable.Math.toFixedPrecise(record[f], typeConfig[type]?.Descriptors?.[f]?.[`Headlight${ action }`]?.DecimalPrecision || this.decimalPrecision, tmpRoundingMethod) || 0;
            }
            else if (f.includes('JSON') || (record[f] && typeof record[f] == 'object'))
            {
                decoratedRecord[f] = 'JSON Object';
            }
            else
            {
                decoratedRecord[f] = (parseInt(f) && record[f] == 0) ? 0 : (record[f] || 'N/A');
            }
        }
        return decoratedRecord;
    }

    _buildDefaultTemplate(manifest, type, record)
    {
        let outputTemplate = ``;
        for (const k of Object.keys(manifest.Descriptors))
        {
            outputTemplate += /*html*/`
                <div class="detail-wrapper px-2 py-1" style="border-bottom: 1px dashed var(--bulma-border);">
                    <div class="is-size-7 has-text-text-50">${ manifest.Descriptors[k].Name }</div>
                    <div class="is-size-6">${ k.startsWith('_') && record[`${ k }_Link`] ? `<span class="icon has-text-info"><i class="fas fa-external-link-alt"></i></span> <a target="_blank" class="p-0 has-text-info is-underlined is-inline" href="{~D:Record.${ k }_Link~}">` : '' }${ manifest.Descriptors[k]?.HeadlightSummary?.ValueTemplate || `{~D:Record.${ k }:N/A~}`}${ k.startsWith('_') ? `</a>` : '' }</div>
                </div>
            `;
        }
        return /*html*/`
        <div class="has-text-centered has-background-info has-text-info-100 p-1"><b>${ manifest.TitleTemplate || this._getHumanReadableFieldName(type) } Summary</b></div>
        <aside class="mt-1" style="max-height: 50vh; overflow-y: auto;">${ outputTemplate }</aside>`;
    }

    /**
	 * @description Creates a popover of the entity record for a particular entity
	 * @param {Object} pOptions - Options for the quick summary.
	 * @param {string} pOptions.type - The entity type being summarized.
     * @param {number} pOptions.ID - The ID of the entity, can be used instead of passing a record.
     * @param {string} pOptions.record - An entity record, can be passed instead of the ID to avoid having to look up the base record.
	 * @param {string} pOptions.parentID - The ID of the element to anchor the popover to.
     * @param {object} pOptions.popoverOptions - Options to pass on to the popover service.
	 */
    async spawnQuickSummary(pOptions)
    {
        if (!pOptions.type || !pOptions.parentID)
        {
            // Need the entity type and a parent element to continue.
            return;
        }
        if (!pOptions.ID && !pOptions.record)
        {
            // Need either a record ID or a record to continue.
            return;
        }
        // If no manifest is set, try to load the manifest from the db.
        if (!this.typeConfig[pOptions.type])
        {
            await this.fable.ManifestPackagingService.loadQuickSummaryManifests([pOptions.type]);
        }
        // If there was still no manifest in the db, create one using the schema endpoint.
        if (!this.typeConfig[pOptions.type])
        {
            this.typeConfig[pOptions.type] = await this._generateDefaultManifest(pOptions.type);
        }
        try
        {
            const localRecord = await this.processRecord(pOptions.type, pOptions.record || await this._getEntity(pOptions.type, pOptions.ID, true));
            if (this.typeConfig[pOptions.type]?.Template)
            {
                this.fable.TemplateProvider.addTemplate('Quick-Summary-Temporary-Template', this.typeConfig[pOptions.type]?.Template);
            }
            else
            {
                this.fable.TemplateProvider.addTemplate('Quick-Summary-Temporary-Template', this._buildDefaultTemplate(this.typeConfig[pOptions.type], pOptions.type, localRecord));
            }
            this.fable.HeadlightPopover.createPopover(Object.assign({
                PopoverDestinationAddress: pOptions.parentID,
                PopoverTemplateHash: 'Quick-Summary-Temporary-Template',
                PopoverTemplateRecord: localRecord,
                theme: 'dropdown',
                placement: 'left',
                maxWidth: '800px',
                delay: [ 500, 0 ], // delay showing the menu to allow for click events to propagate, avoid accidental hover displays, etc.
            }, pOptions.popoverOptions || {}));
        }
        catch (e)
        {
            console.log('Error fetching record: ', e);
            console.log('Passed Options :', pOptions);
            return;
        }
    }

	_getHumanReadableEntityName(pEntity)
	{
		return pEntity.replace(/([a-z])([A-Z])/g, '$1 $2'); // Add space before capital letters
	}

	_getHumanReadableFieldName(field, type)
	{
        if (field.includes('JSON'))
        {
            field = field.replaceAll('JSON', '');
        }
        if (this.typeConfig[type]?.Descriptors?.[field]?.Name)
        {
            return this.typeConfig[type].Descriptors[field].Name;
        }
		if (field === `ID${ type }`)
		{
			return `${ this._getHumanReadableEntityName(type) } Unique Database ID`;
		}
		if (field === `GUID${this.options.Entity}`)
		{
			return `${ this._getHumanReadableEntityName(type) } Unique Identifier`;
		}
		if (field === 'CreatingIDUser')
		{
			return 'Created By User';
		}
		if (field === 'CreateDate')
		{
			return 'Date Created';
		}
		if (field === 'UpdatingIDUser')
		{
			return 'Last Updated By User';
		}
		if (field === 'UpdateDate')
		{
			return 'Date Last Updated';
		}
		if (field.startsWith('ID'))
		{
			return this._getHumanReadableEntityName(field.split('ID')[1]);
		}
		if (field.startsWith('ParentID'))
		{
			return 'Parent ' + this._getHumanReadableEntityName(field.split('ID')[1]);
		}
        if (field.startsWith('ChildID'))
		{
			return 'Child ' + this._getHumanReadableEntityName(field.split('ID')[1]);
		}
		return field.replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
	}
}

module.exports = HeadlightModalFableService;
module.exports.default_configuration = {};
